import * as React from 'react';
import { BrowserEventName, ReactEventName } from '../gen-src/preslayer/Enums';
import { EventHandleSpec } from './EventHandleSpec';
import { Logger } from './utility/VizclientUtils';
import { MiscUtil } from './utility/MiscUtil';
import { NormalizedEvent } from './event/NormalizedEvent';
import { TableauEventHandler } from './TableauEventHandler';
import { TypeUtil } from './utility/TypeUtil';

/**
 *
 * This subclass adds additional hooks to TableauEventHandler for React to use.
 * With react, the actual DOM element won't be available right away (React uses virtual DOM), therefore
 * operation involving the actual DOM element will need to be handled on some later time (or simply on demand).
 *
 */
class TableauEventHandlerForReact extends TableauEventHandler {
  private static browserEventToReactEvent: { [key in BrowserEventName]?: ReactEventName } = null;
  private eventHandlingAttributesForReact: React.HTMLAttributes<object>;

  private constructor() {
    super();
    this.eventHandlingAttributesForReact = this.GetEventHandlingAttributesForReact();
  }

  public get EventHandlingAttributesForReact(): React.HTMLAttributes<object> {
    return this.eventHandlingAttributesForReact;
  }

  /**
  * Factory function for when the caller could not provide the DOM element and the event handling
  * specs at the same time. This allows the caller to call UpdateEventHandleSpec at some later time.
  * React uses this so it can update the spec once it actually has a DOM element that is rendered.
  */
  public static CreateEmpty(): TableauEventHandlerForReact {
    return new TableauEventHandlerForReact();
  }

  /**
  * Useful when creating the TableauEventHandler and specifying the domElement + specs needs
  * to be separate. React components can call this once its dom element is actually rendered.
  * @param {HTMLElement} domElement - Element on which the event handler should be attached upon.
  * @param {EventHandleSpec} config - Event handling specification.
  */
  public UpdateEventHandleSpec(domElement: HTMLElement, config: EventHandleSpec): void {
    this.config = !MiscUtil.IsNullOrUndefined(config) ? config : new EventHandleSpec();
    this.config.DomElement = domElement;
    this.ConfigureDomElem(domElement);
    // If we have a press Drag, set up the callback modifications
    this.SetupEventRecognizer(this.config);
  }

  /**
  * A mapping between the standard browser event names and event names recognized by React.
  * We need to know this to forward the correct event names from react components to the TableauEventHandler.
  */
  private static LookupReactEventName(browserEventName: BrowserEventName): ReactEventName {
    if (MiscUtil.IsNullOrUndefined(TableauEventHandlerForReact.browserEventToReactEvent)) {
      TableauEventHandlerForReact.browserEventToReactEvent = {};
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.TouchStart] = ReactEventName.onTouchStart;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.TouchMove] = ReactEventName.onTouchMove;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.TouchEnd] = ReactEventName.onTouchEnd;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.TouchCancel] = ReactEventName.onTouchCancel;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.MouseDown] = ReactEventName.onMouseDown;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.MouseMove] = ReactEventName.onMouseMove;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.MouseUp] = ReactEventName.onMouseUp;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.MouseOut] = ReactEventName.onMouseOut;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.Scroll] = ReactEventName.onScroll;
      TableauEventHandlerForReact.browserEventToReactEvent[BrowserEventName.ContextMenu] = ReactEventName.onContextMenu;
    }
    return TableauEventHandlerForReact.browserEventToReactEvent[browserEventName];
  }


  /**
  * Get attributes containing event handlers that will forward events which TableauEventHandler cares about.
  * @returns Base HTMLAttributes object that will contain event handlers that forward react events to TableauEventHandler.
  */
  private GetEventHandlingAttributesForReact(): React.HTMLAttributes<object> {
    let attrs: React.HTMLAttributes<object> = {};
    let handler: (_: React.SyntheticEvent<HTMLDivElement>) => void = evt => {
      let e: NormalizedEvent = NormalizedEvent.Create(evt.nativeEvent as TouchEvent, this.config.DomElement, evt);
      this.handler(e);
    };
    NormalizedEvent.AllHandledEvents.forEach(function (handledEvent: BrowserEventName): void {
      let reactEvent: ReactEventName = TableauEventHandlerForReact.LookupReactEventName(handledEvent);
      if (!MiscUtil.IsNullOrUndefined(reactEvent)) {
        TypeUtil.SetField(attrs, reactEvent.toString(), handler);
      } else {
        Logger.Debug(`Can't find a matching React.js event name for ${handledEvent}`);
      }
    });
    return attrs;
  }
}

export { TableauEventHandlerForReact };
